import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, Loader2, TrendingUp, Package, Target } from "lucide-react";
import api from "../../lib/api";

/**
 * QrAnalyticsPanel — Admin → System → QR analytics.
 *
 * Per-target rollup of scans + conversions for every product / campaign
 * QR that has been distributed on flyers, posters, or in-store displays.
 * Answers: "Which flyer is actually driving revenue?"
 */
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const QrAnalyticsPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    const load = async (d = days) => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/qr/analytics?days=${d}`);
            setData(r.data || { totals: {}, rows: [] });
        } catch (e) {
            toast.error("Failed to load QR analytics");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(days); }, [days]);

    const rows = data?.rows || [];
    const totals = data?.totals || { scans: 0, unique_scans: 0, conversions: 0, revenue: 0 };
    const conversionRate = totals.unique_scans > 0
        ? ((totals.conversions / totals.unique_scans) * 100).toFixed(1)
        : "0.0";

    return (
        <div className="space-y-6" data-testid="qr-analytics-panel">
            <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                        <QrCode className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">QR analytics</h2>
                        <p className="text-sm text-[#4A5568] mt-1">
                            Track which flyer, poster, or in-store QR is actually converting. Scans are anonymised (IP + user-agent hashed per day)
                            and conversions are attributed for 60 minutes after a scan.
                        </p>
                    </div>
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="qr-analytics-range"
                        className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm focus:outline-none focus:border-[#E53935]"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last year</option>
                    </select>
                </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile label="Total scans" value={totals.scans} testid="qr-stat-scans" />
                <StatTile label="Unique scanners" value={totals.unique_scans} testid="qr-stat-unique" />
                <StatTile label="Conversions" value={totals.conversions} testid="qr-stat-conversions" hint={`${conversionRate}%`} />
                <StatTile label="Revenue attributed" value={currency(totals.revenue)} testid="qr-stat-revenue" accent />
            </div>

            {/* Rows */}
            <div className="bg-white border border-black/10 rounded-2xl">
                <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">Per QR target</h3>
                    <span className="text-xs text-[#4A5568]">{rows.length} targets</span>
                </div>
                {loading ? (
                    <div className="py-12 text-center text-[#4A5568] flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-12 text-center text-sm text-[#4A5568]">
                        No QR scans in the last {days} day{days === 1 ? "" : "s"} yet. Distribute a poster and watch this page fill up.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-[10px] uppercase tracking-wider text-[#4A5568] border-b border-black/5">
                                <tr>
                                    <th className="px-5 py-2 font-semibold">Target</th>
                                    <th className="px-2 py-2 font-semibold">Type</th>
                                    <th className="px-2 py-2 font-semibold text-right">Scans</th>
                                    <th className="px-2 py-2 font-semibold text-right">Unique</th>
                                    <th className="px-2 py-2 font-semibold text-right">Converted</th>
                                    <th className="px-2 py-2 font-semibold text-right">CVR</th>
                                    <th className="px-5 py-2 font-semibold text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {rows.map((r) => (
                                    <tr key={`${r.kind}-${r.target_id}`} data-testid={`qr-row-${r.target_id}`} className="hover:bg-[#F4F1EA]/40">
                                        <td className="px-5 py-3">
                                            <p className="font-medium text-[#1C252E] truncate max-w-[280px]">{r.name}</p>
                                            <p className="text-xs text-[#4A5568] font-mono truncate max-w-[280px]">{r.target_id}</p>
                                        </td>
                                        <td className="px-2 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                r.kind === "product"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-emerald-100 text-emerald-700"
                                            }`}>
                                                {r.kind === "product" ? <Package className="w-3 h-3" /> : <Target className="w-3 h-3" />}
                                                {r.kind}
                                            </span>
                                        </td>
                                        <td className="px-2 py-3 text-right font-mono text-xs">{r.scans}</td>
                                        <td className="px-2 py-3 text-right font-mono text-xs">{r.unique_scans}</td>
                                        <td className="px-2 py-3 text-right font-mono text-xs font-semibold text-[#1C252E]">{r.conversions}</td>
                                        <td className="px-2 py-3 text-right">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                                r.conversion_rate >= 10 ? "bg-emerald-100 text-emerald-800"
                                                : r.conversion_rate >= 3 ? "bg-amber-100 text-amber-800"
                                                : "bg-gray-100 text-gray-700"
                                            }`}>
                                                {r.conversion_rate.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono font-bold text-[#E53935]">{currency(r.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const StatTile = ({ label, value, hint, testid, accent }) => (
    <div
        data-testid={testid}
        className={`rounded-xl px-4 py-3 border ${accent ? "bg-[#F2D8D5]/50 border-[#E53935]/20" : "bg-white border-black/5"}`}
    >
        <p className={`text-[10px] uppercase tracking-wider font-bold ${accent ? "text-[#E53935]" : "text-[#4A5568]"}`}>{label}</p>
        <p className="font-heading text-2xl text-[#1C252E] font-medium mt-0.5">
            {value}
            {hint ? <span className="text-xs text-[#4A5568] ml-2 font-normal font-body">{hint}</span> : null}
        </p>
    </div>
);

export default QrAnalyticsPanel;
